export type Role =
  | "tech_admin"
  | "owner"
  | "op_manager"
  | "op_supervisor"
  | "op"
  | "finance"
  | "account_manager"
  | "tech_admin";

export interface User {
  id: string;
  name: string;
  email: string;
  initial: string;
  /** 当前激活角色（多角色用户取最高优先级 · 可由 switchRole 切换） */
  role: Role;
  /** 后端返回的全部角色（决定 sidebar / 权限可见性的源头） */
  roleCodes?: Role[];
  tenantSlug?: string;
  mfaEnabled?: boolean;
  avatar?: string;
  /**
   * RBAC-ENF1 · 后端 /auth/me 返回的权限点并集（侧栏 / 页面 / 按钮按它显示）。
   * undefined = 旧会话或 mock，暂不隐藏任何东西（/me 回来后立即收紧）。
   */
  permissions?: string[];
  /** RBACROLE-1 · 角色码 → 中文名（含自定义角色），「没有权限」页用 */
  roleLabels?: Record<string, string>;
}

export const ROLE_LABEL: Record<Role, string> = {
  tech_admin: "超级管理员",
  owner: "老板",
  op_manager: "OP 经理",
  op_supervisor: "OP 主管",
  op: "OP",
  finance: "财务",
  account_manager: "客户经理",
};

/**
 * 多角色优先级（从高到低）· 用于把后端 role_codes[] 降为前端单一 role
 * tech_admin 最高（系统超管·super_admin 已并入）· owner 次之 · op 最低
 */
const ROLE_PRIORITY: Role[] = [
  "tech_admin",
  "owner",
  "op_manager",
  "op_supervisor",
  "finance",
  "account_manager",
  "op",
];

/**
 * 后端真实角色码 → 前端 Role 枚举别名（后端 rbac/roles.py 与前端枚举历史不同步）。
 * op_assistant（运营专员=普通 OP）→ op；customer_manager（客户经理）→ account_manager。
 * 不映射会双双 fallback 成 "op"，导致客户经理误进 OP 工作台。
 */
const BACKEND_ROLE_ALIAS: Record<string, Role> = {
  op_assistant: "op",
  customer_manager: "account_manager",
};

/**
 * 🔴 反向：前端 Role 别名 → 后端真实角色码。**提交前必须过这一层。**
 *
 * 2026-09-10 事故：新建/编辑用户时前端把别名 `op` / `account_manager` 原样 POST 给后端，
 * 而 `roles` 表里真码是 `op_assistant` / `customer_manager`，`users` 上又有外键
 * `fk_users_primary_role → roles(tenant_id, code)` → 一律 409「primary_role 不存在」。
 * 显示侧因为有上面的 BACKEND_ROLE_ALIAS 一切正常，所以只有「建/改」会撞，藏了很久；
 * 而普通员工要选的正是 `op`，等于最常用的那个角色根本配不出账号。
 *
 * 这里从 BACKEND_ROLE_ALIAS **推导**而不是再手写一张表——两张表迟早会漂移。
 */
const FRONTEND_ROLE_TO_BACKEND: Record<string, string> = Object.fromEntries(
  Object.entries(BACKEND_ROLE_ALIAS).map(([backendCode, frontendAlias]) => [frontendAlias, backendCode]),
);

/** 前端 Role → 后端真码（没有别名的原样返回）。写库路径（POST/PATCH）都要用。 */
export function toBackendRole(role: string): string {
  return FRONTEND_ROLE_TO_BACKEND[role] ?? role;
}

/** 后端真码 → 前端 Role 别名（比较 / 分组时用，避免拿别名去比原始码）。 */
export function toFrontendRole(code: string): Role {
  return (BACKEND_ROLE_ALIAS[code] ?? code) as Role;
}

export function pickPrimaryRole(roleCodes: string[] | undefined): Role {
  if (!roleCodes || roleCodes.length === 0) return "op";
  const normalized = roleCodes.map((c) => BACKEND_ROLE_ALIAS[c] ?? c);
  for (const r of ROLE_PRIORITY) {
    if (normalized.includes(r)) return r;
  }
  // 后端返回了未知 role · fallback
  return "op";
}

/** 后端 /me 返回 shape (per apps/api-server/src/api_server/auth/routes.py · MeResponse) */
export interface MeResponseDTO {
  user_id: string;
  email: string;
  role_codes: string[];
  tenant_slug: string;
  mfa_enabled: boolean;
  permissions?: string[];
  role_labels?: Record<string, string>;
}

/** 后端 /refresh + /mfa/verify 返回 shape (TokenResponse · 恒有令牌) */
export interface TokenResponseDTO {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

/**
 * 后端 /login 返回 shape (LoginResponse · MFAFIX-1)。
 * 两种形状靠 mfa_enrollment_required 区分：
 * - 正常登录成功：access_token/refresh_token 有值
 * - 死锁分支（角色强制 MFA 但账号还没绑定）：只有 enrollment_token 有值
 */
export interface LoginResponseDTO {
  access_token?: string;
  refresh_token?: string;
  expires_in: number;
  token_type?: string;
  mfa_enrollment_required?: boolean;
  enrollment_token?: string;
}

/** 后端 /mfa/setup 返回 shape (MfaSetupResponse) */
export interface MfaSetupResponseDTO {
  secret: string;
  provisioning_uri: string;
}

/** /login 请求体 */
export interface LoginRequestDTO {
  email: string;
  password: string;
  mfa_code?: string;
}

export function meToUser(me: MeResponseDTO): User {
  const role = pickPrimaryRole(me.role_codes);
  const namePart = me.email.split("@")[0] ?? me.email;
  return {
    id: me.user_id,
    email: me.email,
    name: namePart,
    initial: namePart.charAt(0).toUpperCase(),
    role,
    roleCodes: me.role_codes as Role[],
    tenantSlug: me.tenant_slug,
    mfaEnabled: me.mfa_enabled,
    permissions: me.permissions,
    roleLabels: me.role_labels,
  };
}
